class Global {
    static entries<T extends object>(t: T) {
        return Object.entries(t) as [keyof T, T[keyof T]][];
    }

    static keys<T extends object>(t: T) {
        return Object.keys(t) as (keyof T)[];
    }

    static values<T extends object>(t: T) {
        return Object.values(t);
    }
}

interface EnumUtils<T> {
    getKey(value: T[keyof T]): keyof T;
    getKeyOr<V, O>(value: V, or: O): V extends T[keyof T] ? keyof T : O;
    getValue(key: keyof T): T[typeof key];
    getValueOr<K, O>(key: K, or: O): K extends keyof T ? T[keyof T] : O;
    getEntry(key: keyof T): [keyof T, T[keyof T]];
    getEntry(value: T[keyof T]): [keyof T, T[keyof T]];
    getKeys(): (keyof T)[];
    getValues(): T[keyof T][];
    getEntries(): [keyof T, T[keyof T]][];
    hasKey(key: any): boolean;
    hasValue(value: any): boolean;
}

export class EnumClass<T extends object> implements EnumUtils<T> {
    private readonly t: T;

    constructor(t: T) {
        this.t = t;
    }

    static create = <T extends object>(t: T) => new EnumClass(t);

    static mergeEnums = <T extends object, U extends Record<keyof T, string>>(
        Id: T,
        Title: U
    ): EnumValue<T, U>[] =>
        EnumClass.create(Id)
            .getEntries()
            .map(([key, Id]) => ({ Id, Title: Title[key] }));

    getKey(value: T[keyof T]): keyof T {
        const index = Global.values(this.t).indexOf(value);
        return Global.entries(this.t)[index][0];
    }

    getKeyOr<V, O>(value: V, or: O): V extends T[keyof T] ? keyof T : O {
        const entry = Global.entries(this.t).find(
            // @ts-ignore
            ([, value1]) => value === value1
        );
        // @ts-ignore
        return entry?.[0] || or;
    }

    getValue(key: keyof T): T[typeof key] {
        return this.t[key];
    }

    getValueOr<K, O>(key: K, or: O): K extends keyof T ? T[keyof T] : O {
        // @ts-ignore
        const entry = Global.entries(this.t).find(([key1]) => key === key1);
        // @ts-ignore
        return entry?.[1] || or;
    }

    getKeys(): (keyof T)[] {
        return Global.keys(this.t);
    }

    getValues(): T[keyof T][] {
        return Global.values(this.t);
    }

    getEntries(): [keyof T, T[keyof T]][] {
        return Global.entries(this.t);
    }

    getEntry(value: T[keyof T]): [keyof T, T[keyof T]];
    getEntry(key: keyof T): [keyof T, T[keyof T]];
    getEntry(kv: any) {
        if (kv in this.t) {
            const k = kv as keyof T;
            return [k, this.t[k]];
        } else {
            const v = kv as T[keyof T];
            const k = this.getKey(v);
            return [k, v];
        }
    }

    hasKey(key: any): boolean {
        return !!this.getValueOr(key, false);
    }

    hasValue(value: any): boolean {
        return !!this.getKeyOr(value, false);
    }

    indexOf(kv: T[keyof T] | keyof T): number {
        if (this.hasKey(kv)) {
            return this.getKeys().indexOf(kv as any);
        } else {
            return this.getValues().indexOf(kv as any);
        }
    }

    indexOfKey(key: keyof T): number {
        return this.getKeys().indexOf(key);
    }

    indexOfValue(value: T[keyof T]): number {
        return this.getValues().indexOf(value);
    }

    valueAt(index: number): T[keyof T] {
        return this.getValues()[index];
    }

    keyAt(index: number): keyof T {
        return this.getKeys()[index];
    }

    /**
     * Returns true if all values in `other` enum are equal to this enum, false otherwise.
     *
     * The compare is shallow.
     * @param other
     */
    isEqual(other: T): boolean {
        return EnumClass.create(other)
            .getEntries()
            .reduce(
                (previousValue, [key, value]) =>
                    previousValue && this.getValue(key) === value,
                true as boolean
            );
    }

    static fromEntries<T extends object>(entries: [keyof T, T[keyof T]][]): T {
        return Object.fromEntries(entries) as any;
    }

    static entries<T extends object>(object: T): [keyof T, T[keyof T]][] {
        return Object.entries(object) as any;
    }

    static keys<T extends object>(object: T): (keyof T)[] {
        return Object.keys(object) as any;
    }

    static values<T extends object>(object: T): T[keyof T][] {
        return Object.keys(object) as any;
    }
}

export type EnumValue<I, T extends Record<keyof I, string>> = {
    Id: I[keyof I];
    Title: T[keyof I];
};
